/**
 * The To-dos page, the home card and counts, and Messages each pass their
 * task list through this, so a bag item to-do whose group this viewer can no
 * longer open (archived, or her family taken out) is not drawn or counted
 * (withoutClosedBagItemTasks in bagItemTask.ts says why).
 *
 * It reads the viewer's bag groups ONLY when the list holds a bag item to-do,
 * so every other viewer pays for no extra group listener. It reuses the bag
 * groups page's own query (useBagGroupsForUser).
 */
import { useMemo } from 'react';
import { useAuth } from './auth-context';
import { useBagGroupsForUser } from './bagGroups';
import { useUserDoc, type UserDoc } from './users';
import { useMyOpenTasks } from './tasks';
import { bagItemTaskGroupId, withoutClosedBagItemTasks } from './bagItemTask';

type BagTaskLike = { id: string; bagGroupId?: string | null; bagItemId?: string | null };

function useOpenableBagItemTasks<T extends BagTaskLike>(tasks: T[], viewer: UserDoc | null): T[] {
  const hasBagTask = useMemo(() => tasks.some((task) => bagItemTaskGroupId(task) !== null), [tasks]);
  const { groups, loading, error, settledForUid } = useBagGroupsForUser(hasBagTask ? viewer : null);
  const viewerUid = viewer?.uid ?? null;
  const liveIds = useMemo(() => {
    // Only an answer that has settled for THIS viewer: the frame before a
    // fresh read starts still carries the empty answer for nobody.
    if (!hasBagTask || loading || error || !viewerUid || settledForUid !== viewerUid) return null;
    return new Set(groups.filter((group) => group.status === 'active').map((group) => group.id));
  }, [hasBagTask, loading, error, viewerUid, settledForUid, groups]);
  return useMemo(
    () => (liveIds ? withoutClosedBagItemTasks(tasks, liveIds) : tasks),
    [tasks, liveIds]
  );
}

/**
 * Any list, any viewer (the To-dos page and Messages, where staff also see
 * other people's to-dos): staff read every group, a family her active ones.
 */
export function useWithoutClosedBagItemTasks<T extends BagTaskLike>(tasks: T[]): T[] {
  const { realUserDoc } = useUserDoc();
  return useOpenableBagItemTasks(tasks, realUserDoc);
}

/**
 * useMyOpenTasks, with a closed group's bag to-do left out BEFORE the list is
 * cut to maxCount (BG-8 refute round 2, minor 2). Cutting first let three
 * stale bag to-dos fill a three-row home card that then read "To do List
 * Complete!" over other open to-dos, and let the home badges count to-dos
 * the list no longer draws. Every home count and card reads through this.
 *
 * Only HER OWN to-dos are here, and a bag to-do is only ever assigned to a
 * member of its group, so the member-scoped query (active groups she is in)
 * answers it for every role. That needs only her uid from sign-in, so this
 * opens no user document listener of its own: the home screens' listener
 * budget (tests/screen-read-budget.test.mjs) is unchanged.
 */
export function useMyOpenTasksStillOpenable(maxCount = 3, enabled = true): ReturnType<typeof useMyOpenTasks> {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  // The shape useBagGroupsForUser keys its query on: role 'family' selects
  // the member-scoped query, nothing more. Never shown or stored.
  const asMember = useMemo(
    () => (uid ? ({ uid, role: 'family', status: 'active' } as UserDoc) : null),
    [uid]
  );
  const result = useMyOpenTasks(Number.POSITIVE_INFINITY, enabled);
  const openable = useOpenableBagItemTasks(result.tasks, asMember);
  // useMyOpenTasks hands back a fresh list every render already, so this
  // adds no render churn of its own.
  return { ...result, tasks: openable.slice(0, maxCount) };
}
